'use client';

import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';

// Backend integration point: GET /api/ehs/incidents/analytics-summary
const monthlyTrend = [
  { month: 'Nov 25', incidents: 6, nearMiss: 9 },
  { month: 'Dec 25', incidents: 8, nearMiss: 7 },
  { month: 'Jan 26', incidents: 5, nearMiss: 11 },
  { month: 'Feb 26', incidents: 4, nearMiss: 14 },
  { month: 'Mar 26', incidents: 7, nearMiss: 10 },
  { month: 'Apr 26', incidents: 3, nearMiss: 13 },
];

const severityData = [
  { severity: 'Critical', count: 4, color: '#ef4444' },
  { severity: 'High', count: 11, color: '#f97316' },
  { severity: 'Medium', count: 18, color: '#f59e0b' },
  { severity: 'Low', count: 9, color: '#22c55e' },
];

const typeData = [
  { id: 'type-slip', name: 'Slip/Trip/Fall', value: 12, color: '#f59e0b' },
  { id: 'type-chem', name: 'Chemical Exposure', value: 9, color: '#ef4444' },
  { id: 'type-elec', name: 'Electrical', value: 5, color: '#8b5cf6' },
  { id: 'type-struck', name: 'Struck By', value: 8, color: '#3b82f6' },
  { id: 'type-ergo', name: 'Ergonomic', value: 6, color: '#22c55e' },
  { id: 'type-other', name: 'Other', value: 2, color: '#64748b' },
];

const totalType = typeData.reduce((s, d) => s + d.value, 0);

const ChartTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg px-3 py-2.5 shadow-xl text-xs">
      {label && <p className="font-600 text-[hsl(var(--foreground))] mb-1.5">{label}</p>}
      {payload.map((entry: any) => (
        <div key={`ctt-${entry.dataKey ?? entry.name}`} className="flex items-center gap-2 mb-0.5">
          <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color ?? entry.fill }} />
          <span className="text-[hsl(var(--muted-foreground))]">{entry.name}:</span>
          <span className="font-600 tabular-nums">{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function IncidentChartRow() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-5">
      {/* Monthly Trend — 2 cols */}
      <div className="lg:col-span-2 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5">
        <div className="mb-4">
          <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">
            Monthly Incident vs Near-Miss Trend
          </h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            6-month rolling · Near-miss rate rising = proactive culture
          </p>
        </div>
        <ResponsiveContainer width="100%" height={220}>
          <LineChart data={monthlyTrend} margin={{ top: 4, right: 8, left: -12, bottom: 0 }}>
            <defs>
              <linearGradient id="incLine" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.2} />
                <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" strokeOpacity={0.5} />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
            <Tooltip content={<ChartTooltip />} />
            <Legend
              iconType="circle"
              iconSize={8}
              wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
            />
            <Line
              type="monotone"
              dataKey="incidents"
              name="Recordable Incidents"
              stroke="#ef4444"
              strokeWidth={2}
              dot={{ r: 3, fill: '#ef4444' }}
              activeDot={{ r: 5 }}
            />
            <Line
              type="monotone"
              dataKey="nearMiss"
              name="Near-Miss Reports"
              stroke="#f59e0b"
              strokeWidth={2}
              strokeDasharray="4 3"
              dot={{ r: 3, fill: '#f59e0b' }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Incident Type Donut — 1 col */}
      <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5 flex flex-col">
        <div className="mb-3">
          <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">Incident Type Breakdown</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">Q2 2026 · {totalType} total</p>
        </div>
        <div className="relative flex-1 min-h-[160px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={typeData}
                cx="50%"
                cy="50%"
                innerRadius="50%"
                outerRadius="78%"
                paddingAngle={2}
                dataKey="value"
              >
                {typeData.map((entry) => (
                  <Cell key={entry.id} fill={entry.color} fillOpacity={0.9} stroke="none" />
                ))}
              </Pie>
              <Tooltip content={<ChartTooltip />} />
            </PieChart>
          </ResponsiveContainer>
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <span className="text-2xl font-700 tabular-nums text-[hsl(var(--foreground))]">{totalType}</span>
            <span className="text-[10px] text-[hsl(var(--muted-foreground))]">incidents</span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-x-2 gap-y-1.5 mt-3">
          {typeData.map((item) => (
            <div key={`typelegend-${item.id}`} className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: item.color }} />
              <span className="text-[10px] text-[hsl(var(--muted-foreground))] truncate">{item.name}</span>
              <span className="text-[10px] font-600 ml-auto tabular-nums text-[hsl(var(--foreground))]">{item.value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Severity Bar — full width */}
      <div className="lg:col-span-3 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-5">
        <div className="mb-3">
          <h3 className="text-sm font-600 text-[hsl(var(--foreground))]">Incident Severity Distribution</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
            Count by severity classification · Q2 2026
          </p>
        </div>
        <ResponsiveContainer width="100%" height={100}>
          <BarChart
            data={severityData}
            layout="vertical"
            margin={{ top: 0, right: 40, left: 20, bottom: 0 }}
            barCategoryGap="30%"
          >
            <XAxis type="number" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
            <YAxis dataKey="severity" type="category" tick={{ fontSize: 12, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} width={60} />
            <Tooltip content={<ChartTooltip />} />
            <Bar dataKey="count" name="Incidents" radius={[0, 4, 4, 0]} maxBarSize={20}>
              {severityData.map((entry) => (
                <Cell key={`sev-cell-${entry.severity}`} fill={entry.color} fillOpacity={0.85} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}